import { LogOut } from 'lucide-react'
import { Link, useParams } from '#router'
import { Button } from '@/components/ui/button'
import { Separator } from '@/components/ui/separator'
import { usePageTitle } from '@/hooks/use-document-title'
import NotFound from '../not-found'
import {
  findSettingsSection,
  settingsSectionHref,
  settingsSections,
} from './sections'

/**
 * The first-party settings page: a frame, a section nav mirroring the Clerk
 * UserProfile tabs, and the section the URL names. Deliberately unstyled beyond
 * layout — sections render blank until their content lands, and the sign-out
 * wiring trails the Clerk rework, so the logout control is a no-op for now.
 */
export function Settings() {
  const { section: slug } = useParams()
  const section = findSettingsSection(slug)
  usePageTitle(section?.label)

  if (!section) return <NotFound />

  const { Component, label, slug: openSlug } = section
  return (
    <main className="min-h-[calc(100vh-4rem)] bg-background">
      <div className="mx-auto flex w-full max-w-7xl flex-col gap-6 px-4 py-6 md:flex-row md:px-6">
        {/* Definite height on every breakpoint (viewport minus the 4rem header
            and the container's 3rem py-6) so the nav is the scroll region and
            the logout footer stays pinned — on mobile too, and without the
            desktop sticky column spilling its footer below the fold. */}
        <div className="flex h-[calc(100dvh-7rem)] flex-col gap-4 md:sticky md:top-16 md:w-56 md:shrink-0">
          <h1 className="text-2xl font-bold tracking-tight">Settings</h1>
          <nav
            aria-label="Settings sections"
            className="min-h-0 flex-1 overflow-y-auto"
          >
            <ul className="flex flex-col gap-1">
              {settingsSections.map(
                ({ slug: itemSlug, label: itemLabel, Icon }) => (
                  <li key={itemSlug}>
                    <Link
                      href={settingsSectionHref(itemSlug)}
                      aria-current={itemSlug === openSlug ? 'page' : undefined}
                      className="text-muted-foreground hover:bg-accent hover:text-accent-foreground aria-[current=page]:bg-accent aria-[current=page]:text-foreground flex items-center gap-2 rounded-md px-2 py-1.5 text-sm transition-colors aria-[current=page]:font-medium"
                    >
                      <Icon className="size-4 shrink-0" aria-hidden="true" />
                      {itemLabel}
                    </Link>
                  </li>
                ),
              )}
            </ul>
          </nav>
          <div className="relative z-10 mt-auto bg-background pt-2">
            <Separator className="mb-2" />
            <Button
              type="button"
              variant="ghost"
              className="text-muted-foreground w-full justify-start gap-2"
            >
              <LogOut className="size-4 shrink-0" aria-hidden="true" />
              Log out
            </Button>
          </div>
        </div>
        <section
          aria-labelledby="settings-section-heading"
          className="flex min-w-0 flex-1 flex-col gap-4"
        >
          <h2
            id="settings-section-heading"
            className="text-xl font-semibold tracking-tight"
          >
            {label}
          </h2>
          <div className="rounded-lg border p-6">
            <Component />
          </div>
        </section>
      </div>
    </main>
  )
}
